import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { Form, Formik } from 'formik';
import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingButton from '../../../components/LoadingButton';
import { AlertContext } from '../../../components/contexts/WithAlertContext';
import InstalmentPlanForm from '../forms/InstalmentPlanForm';
import { InstalmentPlanFormSchema } from '../forms/schemas';
import InstalmentPlanService from '../../../services/InstalmentPlanService';
import InstalmentPlanPreviewDialog from './InstalmentPlanPreviewDialog';
import { InstalmentPlan } from '../../../types/InstalmentPlan/InstalmentPlan';
import { InstalmentPlanPreview } from '../../../types/InstalmentPlan/InstalmentPlanPreview';

const initialInstalmentPlanPreviewResponse = {
  totalNetAmount: 0,
  instalmentRates: [],
};

const enum STEPS {
  CREATE = 'CREATE',
  PREVIEW = 'PREVIEW',
}

const EditInstalmentPlanDialog = ({
  onSuccess,
  isOpen,
  onCancel,
  instalmentPlan,
}: {
  onSuccess: () => void;
  isOpen: boolean;
  onCancel: () => void;
  instalmentPlan: InstalmentPlan;
}) => {
  const [instalmentPlanPreview, setInstalmentPlanPreview] = useState<InstalmentPlanPreview>(initialInstalmentPlanPreviewResponse);
  const [currentStep, setCurrentStep] = useState<STEPS>(STEPS.CREATE);
  const formRef = useRef(null);

  const { t: tValidations } = useTranslation('validations');

  const { t } = useTranslation(['global', 'instalmentPlans']);

  const schema = InstalmentPlanFormSchema(tValidations);

  const alert = useContext(AlertContext);

  function createInstalmentPlanPreview(values: any, { setSubmitting }: any) {
    const request = { ...values };
    request.metadata = values.metadata;
    InstalmentPlanService.createInstalmentPlanPreview(request)
      .then((response) => {
        setInstalmentPlanPreview(response);
        setSubmitting(false);
        setCurrentStep(STEPS.PREVIEW);
      })
      .catch(() => {
        setSubmitting(false);
        onCancel();
        alert.sendAlert('error', t('instalmentPlans:instalment plan edit error'));
      });
  }

  function updateInstalmentPlan(instalmentPlanId: string, values: any, setSubmitting: any) {
    values.debtorId = values.debitorId;
    const request = { ...values };
    request.metadata = values.metadata;
    InstalmentPlanService.editInstalmentPlanInDraft(instalmentPlanId, request)
      .then(() => {
        setSubmitting(false);
        alert.sendAlert('success', t('instalmentPlans:instalment plan edit success'));
        onSuccess();
      })
      .catch(() => {
        onCancel();
        alert.sendAlert('error', t('instalmentPlans:instalment plan edit error'));
      });
  }
  function createInstalmentPlanObject(instalmentPlanPreview: InstalmentPlanPreview, values: any): InstalmentPlan {
    return {
      downPayment: values.downPayment,
      instalmentRates: instalmentPlanPreview.instalmentRates,
      interval: values.interval,
      intervalAmount: values.intervalAmount,
      intervalDay: values.intervalDay,
      purpose: values.purpose,
      totalNetAmount: instalmentPlanPreview.totalNetAmount,
    } as InstalmentPlan;
  }

  const initialValues = {
    bankAccountId: instalmentPlan.bankAccount.id,
    debitorId: instalmentPlan.debtor.id,
    debitor: instalmentPlan.debtor,
    purpose: instalmentPlan.purpose,
    totalNetAmount: instalmentPlan.totalNetAmount,
    interval: instalmentPlan.interval,
    startDate: instalmentPlan.startDate,
    intervalAmount: instalmentPlan.intervalAmount,
    intervalDay: instalmentPlan.intervalDay,
    downPayment: instalmentPlan.downPayment || undefined,
    requireDownPayment: instalmentPlan.downPayment > 0,
    metadata: instalmentPlan.metadata || [],
  };

  return (
    <Formik innerRef={formRef} initialValues={initialValues} validationSchema={schema} onSubmit={createInstalmentPlanPreview}>
      {({ setFieldValue, setSubmitting, isValid, values, isSubmitting }) => (
        <>
          {currentStep === STEPS.CREATE && (
            <Dialog
              maxWidth="sm"
              fullWidth
              open={isOpen}
              onClose={(event, reason) => {
                if (reason === 'backdropClick') {
                  return false;
                }
                onCancel();
              }}
              aria-labelledby="alert-dialog-title"
            >
              <Form>
                <DialogTitle id="alert-dialog-title">{t('instalmentPlans:edit instalment plan')}</DialogTitle>
                <DialogContent dividers>
                  <InstalmentPlanForm values={values} setFieldValue={setFieldValue} debtor={values.debitor} />
                </DialogContent>
                <DialogActions>
                  <Button onClick={onCancel} color="primary">
                    {t('global:cancel')}
                  </Button>
                  <LoadingButton loading={isSubmitting} type="submit" disabled={!isValid}>
                    {t('instalmentPlans:Preview changes')}
                  </LoadingButton>
                </DialogActions>
              </Form>
            </Dialog>
          )}
          {currentStep === STEPS.PREVIEW && (
            <InstalmentPlanPreviewDialog
              onCancel={() => onCancel()}
              onBack={() => {
                setCurrentStep(STEPS.CREATE);
              }}
              instalmentPlan={createInstalmentPlanObject(instalmentPlanPreview, values)}
              dialogActions={
                <LoadingButton loading={isSubmitting} onClick={() => updateInstalmentPlan(instalmentPlan.id, values, setSubmitting)}>
                  {t('instalmentPlans:save instalment plan')}
                </LoadingButton>
              }
            />
          )}
        </>
      )}
    </Formik>
  );
};
export default EditInstalmentPlanDialog;
